import { useState, useCallback } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { Page } from '@shared/types';

interface PageNavigatorProps {
  pages: Page[];
  currentPageId: string | null;
  onPageSelect: (pageId: string) => void;
  onAddPage: () => void;
  onDeletePage: (pageId: string) => void;
  onReorderPages: (pageIds: string[]) => void;
}

export function PageNavigator({
  pages,
  currentPageId,
  onPageSelect,
  onAddPage,
  onDeletePage,
  onReorderPages,
}: PageNavigatorProps) {
  const [deletePageId, setDeletePageId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [draggedPageId, setDraggedPageId] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, pageId: string) => {
    e.stopPropagation();
    setDeletePageId(pageId);
  };

  const handleConfirmDelete = async () => {
    if (!deletePageId) return;

    setIsDeleting(true);
    try {
      await onDeletePage(deletePageId);
    } finally {
      setIsDeleting(false);
      setDeletePageId(null);
    }
  };

  const handleDragStart = (e: React.DragEvent, pageId: string) => {
    setDraggedPageId(pageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();

    if (!draggedPageId) return;

    const currentIndex = pages.findIndex(p => p.id === draggedPageId);
    if (currentIndex === -1 || currentIndex === targetIndex) {
      setDraggedPageId(null);
      return;
    }

    // Create new page order
    const newPages = [...pages];
    const [draggedPage] = newPages.splice(currentIndex, 1);
    newPages.splice(targetIndex, 0, draggedPage);

    onReorderPages(newPages.map(p => p.id));
    setDraggedPageId(null);
  }, [draggedPageId, pages, onReorderPages]);

  const handleDragEnd = () => {
    setDraggedPageId(null);
  };

  return (
    <>
      <nav className="page-navigator" aria-label="Page navigation">
        <div className="page-navigator-thumbnails">
          {pages.map((page, index) => (
            <div
              key={page.id}
              className={`page-thumbnail ${page.id === currentPageId ? 'active' : ''}`}
              onClick={() => onPageSelect(page.id)}
              draggable
              onDragStart={(e) => handleDragStart(e, page.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              role="button"
              tabIndex={0}
              aria-label={`Page ${index + 1}${page.id === currentPageId ? ' (current)' : ''}`}
              aria-current={page.id === currentPageId ? 'page' : undefined}
            >
              {page.thumbnailUrl ? (
                <img
                  src={page.thumbnailUrl}
                  alt={`Page ${index + 1} thumbnail`}
                  className="page-thumbnail-image"
                />
              ) : (
                <div className="page-thumbnail-placeholder">
                  {index + 1}
                </div>
              )}

              {/* Only show delete button if there's more than one page */}
              {pages.length > 1 && (
                <button
                  className="page-thumbnail-delete"
                  onClick={(e) => handleDeleteClick(e, page.id)}
                  aria-label={`Delete page ${index + 1}`}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              )}
            </div>
          ))}

          <button
            className="page-navigator-add"
            onClick={onAddPage}
            aria-label="Add new page"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
      </nav>

      <ConfirmDialog
        isOpen={deletePageId !== null}
        title="Delete page?"
        message="This action cannot be undone. All content on this page will be permanently deleted."
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletePageId(null)}
      />
    </>
  );
}
